import { SearchIcon, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { RxDiagnosis } from '@/entities/rx';
import { useSearchDiagnosesQuery } from '@/entities/rx';
import { Button } from '@/shared/ui/shadcn/button';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/shadcn/command';
import { Input } from '@/shared/ui/shadcn/input';
import { Label } from '@/shared/ui/shadcn/label';

import type { RxFormApi } from '../../model/types';

interface Props {
  form: RxFormApi;
  requiredForInsuranceApproval?: boolean;
  hideField?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  dialogTitle?: string;
  dialogDescription?: string;
}

interface DiagnosisRow {
  diagnosisId: number;
  code: string;
  name: string;
}

export function DiagnosisSelector({
  form,
  requiredForInsuranceApproval = false,
  hideField = false,
  open,
  onOpenChange,
  dialogTitle = 'Diagnosis',
  dialogDescription = 'Select the condition this medication is treating.',
}: Props) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const isOpen = open ?? internalOpen;

  const setIsOpen = (nextOpen: boolean) => {
    setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const trimmed = searchText.trim();
  const searchQuery = useSearchDiagnosesQuery(trimmed.length >= 2 ? trimmed : undefined);

  const rows = useMemo((): DiagnosisRow[] => {
    return (searchQuery.data ?? [])
      .filter((r) => r.DiagnosisId != null && r.DiagnosisCode)
      .map((r) => ({
        diagnosisId: r.DiagnosisId!,
        code: r.DiagnosisCode!,
        name: r.DiagnosisName ?? '',
      }));
  }, [searchQuery.data]);

  const selected = form.state.values.selectedDiagnosis as RxDiagnosis | null;
  const displayLabel = selected
    ? selected.name
      ? `${selected.name} (${selected.code})`
      : selected.code
    : null;

  const handleSelect = (row: DiagnosisRow) => {
    form.setFieldValue('selectedDiagnosis', {
      diagnosisId: row.diagnosisId,
      code: row.code,
      name: row.name,
    });
    setIsOpen(false);
    setSearchText('');
  };

  const handleClear = () => {
    form.setFieldValue('selectedDiagnosis', null);
  };

  return (
    <div className={hideField ? undefined : 'space-y-1'}>
      {hideField ? null : (
        <>
          <Label htmlFor="prescription-diagnosis">Diagnosis</Label>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                id="prescription-diagnosis"
                readOnly
                value={displayLabel ?? ''}
                placeholder="Search by condition or ICD-10 code"
                onClick={() => setIsOpen(true)}
                onFocus={() => setIsOpen(true)}
                aria-required={requiredForInsuranceApproval}
                className="cursor-pointer pl-9"
              />
            </div>
            {selected ? (
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                onClick={handleClear}
                aria-label="Clear diagnosis"
              >
                <X className="size-4" aria-hidden />
              </Button>
            ) : null}
          </div>
          {requiredForInsuranceApproval ? (
            <p className="text-xs leading-4 text-muted-foreground">
              Needed only if requesting insurance approval.
            </p>
          ) : null}
        </>
      )}

      <CommandDialog
        open={isOpen}
        onOpenChange={(nextOpen) => {
          setIsOpen(nextOpen);
          if (!nextOpen) {
            setSearchText('');
          }
        }}
        title={dialogTitle}
        description={dialogDescription}
        showHeader
        className="gap-0 [&_[data-slot=command]]:pb-4"
      >
        <CommandInput
          value={searchText}
          onValueChange={setSearchText}
          placeholder="Search by condition name or ICD-10 code"
          wrapperClassName="mx-4 mt-2 rounded-xl border border-border/80 bg-secondary/30"
          autoFocus
        />
        {trimmed.length === 0 ? null : (
          <CommandList className="mt-2">
            {trimmed.length < 2 ? (
              <CommandEmpty>Enter at least 2 characters.</CommandEmpty>
            ) : searchQuery.isPending ? (
              <CommandEmpty>Searching…</CommandEmpty>
            ) : searchQuery.isError ? (
              <CommandEmpty>
                Search failed.
                <div className="mt-3 flex justify-center">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => void searchQuery.refetch()}
                  >
                    Retry
                  </Button>
                </div>
              </CommandEmpty>
            ) : rows.length === 0 ? (
              <CommandEmpty>No diagnoses found.</CommandEmpty>
            ) : (
              <CommandGroup heading="Results">
                {rows.map((row) => (
                  <CommandItem
                    key={row.diagnosisId}
                    value={`${row.code} ${row.name} ${row.diagnosisId}`}
                    onSelect={() => handleSelect(row)}
                  >
                    <div className="flex min-w-0 flex-col">
                      <div className="truncate font-medium">{row.name || row.code}</div>
                      <div className="text-xs text-muted-foreground">ICD-10 {row.code}</div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        )}
      </CommandDialog>
    </div>
  );
}
